import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// Lazy-loaded Danish Master Pages
const Home = lazy(() => import('./pages/Home'));
const Demo = lazy(() => import('./pages/Demo'));
const Produkt = lazy(() => import('./pages/Produkt'));
const Tilbehoer = lazy(() => import('./pages/Tilbehoer'));
const Leje = lazy(() => import('./pages/Leje'));
const Erhvervsleasing = lazy(() => import('./pages/Erhvervsleasing'));
const Privatfinansiering = lazy(() => import('./pages/Privatfinansiering'));
const Service = lazy(() => import('./pages/Service'));
const MoedOsHer = lazy(() => import('./pages/MoedOsHer'));
const Nyheder = lazy(() => import('./pages/Nyheder'));
const NyhedDetalje = lazy(() => import('./pages/NyhedDetalje'));
const Kundeudtalelser = lazy(() => import('./pages/Kundeudtalelser'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Kontakt = lazy(() => import('./pages/Kontakt'));
const OmDanshaker = lazy(() => import('./pages/OmDanshaker'));
const BeregnerPage = lazy(() => import('./pages/BeregnerPage'));

// Lazy-loaded English Localized Pages
const HomeEn = lazy(() => import('./pages/en/HomeEn'));
const DemoEn = lazy(() => import('./pages/en/DemoEn'));
const ProduktEn = lazy(() => import('./pages/en/ProduktEn'));
const TilbehoerEn = lazy(() => import('./pages/en/TilbehoerEn'));
const LejeEn = lazy(() => import('./pages/en/LejeEn'));
const ErhvervsleasingEn = lazy(() => import('./pages/en/ErhvervsleasingEn'));
const PrivatfinansieringEn = lazy(() => import('./pages/en/PrivatfinansieringEn'));
const ServiceEn = lazy(() => import('./pages/en/ServiceEn'));
const MoedOsHerEn = lazy(() => import('./pages/en/MoedOsHerEn'));
const NyhederEn = lazy(() => import('./pages/en/NyhederEn'));
const NyhedDetaljeEn = lazy(() => import('./pages/en/NyhedDetaljeEn'));
const KundeudtalelserEn = lazy(() => import('./pages/en/KundeudtalelserEn'));
const FAQEn = lazy(() => import('./pages/en/FAQEn'));
const KontaktEn = lazy(() => import('./pages/en/KontaktEn'));
const OmDanshakerEn = lazy(() => import('./pages/en/OmDanshakerEn'));
const BeregnerPageEn = lazy(() => import('./pages/en/BeregnerPageEn'));

// Lazy-loaded German Localized Pages
const HomeDe = lazy(() => import('./pages/de/HomeDe'));
const DemoDe = lazy(() => import('./pages/de/DemoDe'));
const ProduktDe = lazy(() => import('./pages/de/ProduktDe'));
const TilbehoerDe = lazy(() => import('./pages/de/TilbehoerDe'));
const LejeDe = lazy(() => import('./pages/de/LejeDe'));
const ErhvervsleasingDe = lazy(() => import('./pages/de/ErhvervsleasingDe'));
const PrivatfinansieringDe = lazy(() => import('./pages/de/PrivatfinansieringDe'));
const ServiceDe = lazy(() => import('./pages/de/ServiceDe'));
const MoedOsHerDe = lazy(() => import('./pages/de/MoedOsHerDe'));
const NyhederDe = lazy(() => import('./pages/de/NyhederDe'));
const NyhedDetaljeDe = lazy(() => import('./pages/de/NyhedDetaljeDe'));
const KundeudtalelserDe = lazy(() => import('./pages/de/KundeudtalelserDe'));
const FAQDe = lazy(() => import('./pages/de/FAQDe'));
const KontaktDe = lazy(() => import('./pages/de/KontaktDe'));
const OmDanshakerDe = lazy(() => import('./pages/de/OmDanshakerDe'));
const BeregnerPageDe = lazy(() => import('./pages/de/BeregnerPageDe'));

// Lightweight page loader placeholder
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-4 border-[#0E67A2] border-t-transparent rounded-full animate-spin" aria-label="Indlæser side..." />
    </div>
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <div className="relative min-h-screen flex flex-col bg-slate-50 text-slate-900 antialiased selection:bg-[#0E67A2]/20 selection:text-[#0E67A2]">
        {/* Global Header */}
        <Header />

        {/* Main Content Area */}
        <main className="flex-grow">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Danish Master Routes (1:1 Untouched) */}
              <Route path="/" element={<Home />} />
              <Route path="/gratis-demo" element={<Demo />} />
              <Route path="/produkt" element={<Produkt />} />
              <Route path="/tilbehoer" element={<Tilbehoer />} />
              <Route path="/leje" element={<Leje />} />
              <Route path="/erhvervsleasing" element={<Erhvervsleasing />} />
              <Route path="/privatfinansiering" element={<Privatfinansiering />} />
              <Route path="/service" element={<Service />} />
              <Route path="/moed-os-her" element={<MoedOsHer />} />
              <Route path="/nyheder" element={<Nyheder />} />
              <Route path="/nyheder/:slug" element={<NyhedDetalje />} />
              <Route path="/kundeudtalelser" element={<Kundeudtalelser />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/om-danshaker" element={<OmDanshaker />} />
              <Route path="/beregner" element={<BeregnerPage />} />

              {/* English Localized Routes (/en/...) */}
              <Route path="/en" element={<HomeEn />} />
              <Route path="/en/free-demo" element={<DemoEn />} />
              <Route path="/en/product" element={<ProduktEn />} />
              <Route path="/en/accessories" element={<TilbehoerEn />} />
              <Route path="/en/rent" element={<LejeEn />} />
              <Route path="/en/commercial-leasing" element={<ErhvervsleasingEn />} />
              <Route path="/en/private-financing" element={<PrivatfinansieringEn />} />
              <Route path="/en/service" element={<ServiceEn />} />
              <Route path="/en/meet-us" element={<MoedOsHerEn />} />
              <Route path="/en/news" element={<NyhederEn />} />
              <Route path="/en/news/:slug" element={<NyhedDetaljeEn />} />
              <Route path="/en/reviews" element={<KundeudtalelserEn />} />
              <Route path="/en/faq" element={<FAQEn />} />
              <Route path="/en/contact" element={<KontaktEn />} />
              <Route path="/en/about-danshaker" element={<OmDanshakerEn />} />
              <Route path="/en/calculator" element={<BeregnerPageEn />} />

              {/* German Localized Routes (/de/...) */}
              <Route path="/de" element={<HomeDe />} />
              <Route path="/de/kostenlose-demo" element={<DemoDe />} />
              <Route path="/de/produkt" element={<ProduktDe />} />
              <Route path="/de/zubehoer" element={<TilbehoerDe />} />
              <Route path="/de/miete" element={<LejeDe />} />
              <Route path="/de/gewerbeleasing" element={<ErhvervsleasingDe />} />
              <Route path="/de/privatfinanzierung" element={<PrivatfinansieringDe />} />
              <Route path="/de/service" element={<ServiceDe />} />
              <Route path="/de/uns-treffen" element={<MoedOsHerDe />} />
              <Route path="/de/neuigkeiten" element={<NyhederDe />} />
              <Route path="/de/neuigkeiten/:slug" element={<NyhedDetaljeDe />} />
              <Route path="/de/kundenstimmen" element={<KundeudtalelserDe />} />
              <Route path="/de/faq" element={<FAQDe />} />
              <Route path="/de/kontakt" element={<KontaktDe />} />
              <Route path="/de/ueber-danshaker" element={<OmDanshakerDe />} />
              <Route path="/de/sparrechner" element={<BeregnerPageDe />} />

              {/* Fallback route */}
              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
        </main>

        {/* Global Footer */}
        <Footer />
      </div>
    </>
  );
}
